/* eslint-disable import/no-unassigned-import */

import { assertPackageVersion } from '@dynamic-labs-sdk/assert-package-version';

import {
  name as packageName,
  version as packageVersion,
} from '../../package.json';
assertPackageVersion(packageName, packageVersion);

// Import the modules states so that they are available in the global scope.
import '../modules/auth/state';
import '../modules/captcha/state';
import '../modules/initializeClient/state';
import '../modules/mfa/state';
import '../modules/projectSettings/state';
import '../modules/sessionKeys/state';
import '../modules/wallets/unverifiedWalletAccounts/state';
import '../modules/wallets/walletProvider/walletProviderKeyMap/state';
import '../utils/getNonce/state';

// Import the event types for the modules so that they are available in the global scope.
import '../modules/auth/events';
import '../modules/deviceRegistration/events';
import '../modules/mfa/events';
import '../modules/state/raiseStateEvents/events';
import '../modules/wallets/walletAccount/events';
import '../modules/wallets/walletProviderRegistry/events';

export { MFAAction } from '@dynamic-labs/sdk-api-core';
export type {
  CreateMfaToken,
  DeviceRegistrationResponse,
  TokenScope,
  UserPasskey,
  VerifyResponse,
} from '@dynamic-labs/sdk-api-core';
export type { DynamicCoreConfig } from '../client/core/types';
export { createDynamicClient } from '../client/createDynamicClient';
export { getDefaultClient } from '../client/defaultClient';
export type { DynamicClient, DynamicClientConfig } from '../client/types';
export { BaseError, type BaseErrorParameters } from '../errors/base';
export { CheckoutSessionTokenMissingError } from '../errors/CheckoutSessionTokenMissingError';
export { ClientAlreadyInitializedError } from '../errors/ClientAlreadyInitializedError';
export { DeeplinkConnectAndVerifyUnsupportedError } from '../errors/DeeplinkConnectAndVerifyUnsupportedError';
export { FeeEstimationFailedError } from '../errors/FeeEstimationFailedError';
export { InvalidDeviceRegistrationRedirectError } from '../errors/InvalidDeviceRegistrationRedirectError';
export { InvalidExternalAuthError } from '../errors/InvalidExternalAuthError';
export { InvalidParamError } from '../errors/InvalidParamError';
export { InvalidRedirectStorageStateError } from '../errors/InvalidRedirectStorageStateError';
export { LinkCredentialError } from '../errors/LinkCredentialError';
export { MfaInvalidOtpError } from '../errors/MfaInvalidOtpError';
export { MfaRateLimitedError } from '../errors/MfaRateLimitedError';
export { MissingRedirectStorageStateError } from '../errors/MissingRedirectStorageStateError';
export { MissingSocialUrlParamError } from '../errors/MissingSocialUrlParamError';
export { MissingUserVerificationError } from '../errors/MissingUserVerificationError';
export { NetworkAddingUnavailableError } from '../errors/NetworkAddingUnavailableError';
export { NetworkNotAddedError } from '../errors/NetworkNotAddedError';
export { NetworkSwitchingUnavailableError } from '../errors/NetworkSwitchingUnavailableError';
export { NoAddressFoundError } from '../errors/NoAddressFoundError';
export { NoNetworkProvidersError } from '../errors/NoNetworkProvidersError';
export { NoPasskeyCredentialsFoundError } from '../errors/NoPasskeyCredentialsFoundError';
export { NoSmartWalletAccountSignerFoundError } from '../errors/NoSmartWalletAccountSignerFoundError';
export { NotWaasWalletAccountError } from '../errors/NotWaasWalletAccountError';
export { NoWebAuthNSupportError } from '../errors/NoWebAuthNSupportError';
export { SandboxMaximumThresholdReachedError } from '../errors/SandboxMaximumThresholdReachedError';
export { SimulationFailedError } from '../errors/SimulationFailedError';
export { UnavailableInServerSideError } from '../errors/UnavailableInServerSideError';
export { UnauthorizedError } from '../errors/UnauthorizedError';
export { UnrecognizedNetworkError } from '../errors/UnrecognizedNetworkError';
export { UserNotAuthenticatedError } from '../errors/UserNotAuthenticatedError';
export { UserRejectedError } from '../errors/UserRejectedError';
export { WalletAccountAlreadyVerifiedError } from '../errors/WalletAccountAlreadyVerifiedError';
export { WalletAccountNotSelectedError } from '../errors/WalletAccountNotSelectedError';
export { WalletAlreadyLinkedToAnotherUserError } from '../errors/WalletAlreadyLinkedToAnotherUserError';
export { WalletProviderMethodUnavailableError } from '../errors/WalletProviderMethodUnavailableError';
export { signInWithExternalJwt } from '../modules/auth/externalAuth/signInWithExternalJwt';
export { getElevatedAccessToken } from '../modules/auth/getElevatedAccessToken';
export { isSignedIn } from '../modules/auth/isSignedIn';
export { logout } from '../modules/auth/logout';
export { deletePasskey } from '../modules/auth/passkeys/deletePasskey';
export { getPasskeys } from '../modules/auth/passkeys/getPasskeys';
export { registerPasskey } from '../modules/auth/passkeys/registerPasskey';
export { signInWithPasskey } from '../modules/auth/passkeys/signInWithPasskey';
export { getUserSocialAccounts } from '../modules/auth/social/getUserSocialAccounts';
export { authenticateWithSocial } from '../modules/auth/social/oauth/authenticateWithSocial';
export { completeSocialAuthentication } from '../modules/auth/social/oauth/completeSocialAuthentication';
export { detectOAuthRedirect } from '../modules/auth/social/oauth/detectOAuthRedirect';
export { unlinkSocialAccount } from '../modules/auth/social/oauth/unlinkSocialAccount';
export type {
  SocialAccount,
  SocialProvider,
} from '../modules/auth/social/social.types';
export {
  getMultichainBalances,
  type GetMultichainBalancesParams,
} from '../modules/balances/getMultichainBalances';
export { isCaptchaRequired } from '../modules/captcha/isCaptchaRequired';
export { setCaptchaToken } from '../modules/captcha/setCaptchaToken';
export type { Chain } from '../modules/chain';
export { attachCheckoutTransactionSource } from '../modules/checkout/attachCheckoutTransactionSource';
export type { AttachCheckoutTransactionSourceParams } from '../modules/checkout/attachCheckoutTransactionSource';
export { broadcastCheckoutTransaction } from '../modules/checkout/broadcastCheckoutTransaction';
export type { BroadcastCheckoutTransactionParams } from '../modules/checkout/broadcastCheckoutTransaction';
export { cancelCheckoutTransaction } from '../modules/checkout/cancelCheckoutTransaction';
export type { CancelCheckoutTransactionParams } from '../modules/checkout/cancelCheckoutTransaction';
export type {
  CheckoutExecutionState,
  CheckoutFailure,
  CheckoutFeeBreakdown,
  CheckoutQuoteSnapshot,
  CheckoutRiskState,
  CheckoutSettlementData,
  CheckoutSettlementState,
  CheckoutSourceType,
  CheckoutTransaction,
  CheckoutTransactionCreateResponse,
} from '../modules/checkout/checkout.types';
export { createCheckoutTransaction } from '../modules/checkout/createCheckoutTransaction';
export type { CreateCheckoutTransactionParams } from '../modules/checkout/createCheckoutTransaction';
export { getCheckoutTransaction } from '../modules/checkout/getCheckoutTransaction';
export type { GetCheckoutTransactionParams } from '../modules/checkout/getCheckoutTransaction';
export { getCheckoutTransactionQuote } from '../modules/checkout/getCheckoutTransactionQuote';
export type { GetCheckoutTransactionQuoteParams } from '../modules/checkout/getCheckoutTransactionQuote';
export { prepareCheckoutTransaction } from '../modules/checkout/prepareCheckoutTransaction';
export type { PrepareCheckoutTransactionParams } from '../modules/checkout/prepareCheckoutTransaction';
export { submitCheckoutTransaction } from '../modules/checkout/submitCheckoutTransaction';
export type { SubmitCheckoutTransactionParams } from '../modules/checkout/submitCheckoutTransaction';
export { offEvent, onceEvent, onEvent } from '../modules/clientEvents';
export type {
  DynamicEvents,
  OffEventParams,
  OnceEventParams,
  OnEventParams,
} from '../modules/clientEvents/clientEvents.types';
export { completeDeviceRegistration } from '../modules/deviceRegistration/completeDeviceRegistration';
export { detectDeviceRegistrationRedirect } from '../modules/deviceRegistration/detectDeviceRegistrationRedirect';
export { getDeviceRegistrationTokenFromUrl } from '../modules/deviceRegistration/getDeviceRegistrationTokenFromUrl';
export { getRegisteredDevices } from '../modules/deviceRegistration/getRegisteredDevices';
export { isDeviceRegistrationRequired } from '../modules/deviceRegistration/isDeviceRegistrationRequired';
export { revokeAllRegisteredDevices } from '../modules/deviceRegistration/revokeAllRegisteredDevices';
export { revokeRegisteredDevice } from '../modules/deviceRegistration/revokeRegisteredDevice';
export { hasExtension } from '../modules/extension/hasExtension';
export { addCoinbaseOnrampOrderEventListener } from '../modules/funding/coinbase/addCoinbaseOnrampOrderEventListener';
export type {
  CoinbaseCreateOnrampOrderRequest,
  CoinbaseOnrampFeeType,
  CoinbaseOnrampGetBuyUrlRequest,
  CoinbaseOnrampGetBuyUrlResponse,
  CoinbaseOnrampMissingVerificationErrorCode,
  CoinbaseOnrampOrderPaymentLinkType,
  CoinbaseOnrampOrderPaymentMethod,
  CoinbaseOnrampOrderResponse,
  CoinbaseOnrampOrderStatus,
  FieldMissingVerificationForCoinbaseOnramp,
} from '../modules/funding/coinbase/coinbase.types';
export { createCoinbaseOnrampOrder } from '../modules/funding/coinbase/createCoinbaseOnrampOrder';
export { getCoinbaseBuyUrl } from '../modules/funding/coinbase/getCoinbaseBuyUrl';
export { getMissingVerificationForCoinbaseOnrampOrder } from '../modules/funding/coinbase/getMissingVerificationForCoinbaseOnrampOrder';
export { createCryptoDotComPayment } from '../modules/funding/cryptoDotCom/createCryptoDotComPayment';
export type {
  CryptoDotComPaymentCreateRequest,
  CryptoDotComPaymentResponse,
} from '../modules/funding/cryptoDotCom/cryptoDotCom.types';
export {
  createKrakenExchangeTransfer,
  type KrakenTransferRequest,
} from '../modules/funding/kraken/createKrakenExchangeTransfer';
export {
  getKrakenAccounts,
  type GetKrakenAccountsParams,
} from '../modules/funding/kraken/getKrakenAccounts';
export { getKrakenWhitelistedAddresses } from '../modules/funding/kraken/getKrakenWhitelistedAddresses';
export type {
  ExchangeTransferResponse,
  KrakenAccount,
  TransferDestinationResponse,
} from '../modules/funding/kraken/kraken.types';
export { initializeClient } from '../modules/initializeClient';
export type { DynamicInitStatus } from '../modules/initializeClient/state';
export { waitForClientInitialized } from '../modules/initializeClient/waitForClientInitialized';
export { getWalletConnectCatalog } from '../modules/legacyWalletBook/getWalletConnectCatalog';
export type {
  WalletConnectCatalog,
  WalletConnectCatalogDeepLinks,
  WalletConnectCatalogGroup,
  WalletConnectCatalogWallet,
} from '../modules/legacyWalletBook/getWalletConnectCatalog';
export { getWalletConnectCatalogWalletByWalletProviderKey } from '../modules/legacyWalletBook/getWalletConnectCatalogWalletByWalletProviderKey';
export { acknowledgeRecoveryCodes } from '../modules/mfa/acknowledgeRecoveryCodes';
export { authenticateMfaRecoveryCode } from '../modules/mfa/authenticateMfaRecoveryCode';
export { authenticatePasskeyMFA } from '../modules/mfa/authenticatePasskeyMFA';
export { authenticateTotpMfaDevice } from '../modules/mfa/authenticateTotpMfaDevice';
export { consumeMfaToken } from '../modules/mfa/consumeMfaToken';
export { createNewMfaRecoveryCodes } from '../modules/mfa/createNewMfaRecoveryCodes';
export { deleteMfaDevice } from '../modules/mfa/deleteMfaDevice';
export { getMfaDevices } from '../modules/mfa/getMfaDevices';
export { getMfaMethods } from '../modules/mfa/getMfaMethods';
export { getMfaRecoveryCodes } from '../modules/mfa/getMfaRecoveryCodes';
export { isMfaRequiredForAction } from '../modules/mfa/isMfaRequiredForAction';
export { isPendingRecoveryCodesAcknowledgment } from '../modules/mfa/isPendingRecoveryCodesAcknowledgment';
export { isUserMissingMfaAuth } from '../modules/mfa/isUserMissingMfaAuth';
export type { MFADevice } from '../modules/mfa/mfa.types';
export { registerTotpMfaDevice } from '../modules/mfa/registerTotpMfaDevice';
export { setDefaultMfaDevice } from '../modules/mfa/setDefaultMfaDevice';
export type { OTPVerification } from '../modules/otp/otp.types';
export { sendEmailOTP } from '../modules/otp/sendEmailOTP';
export { sendSmsOTP } from '../modules/otp/sendSmsOTP';
export {
  supportedCountries,
  type CountryCode,
} from '../modules/otp/sendSmsOTP/supportedCountries';
export { verifyOTP } from '../modules/otp/verifyOTP';
export { fetchProjectSettings } from '../modules/projectSettings/fetchProjectSettings';
export type {
  BlockaidValidation,
  SimulationResult,
  TransactionFeeData,
} from '../modules/simulation/simulation.types';
export { deleteUser } from '../modules/user/deleteUser';
export { isUserOnboardingComplete } from '../modules/user/isUserOnboardingComplete';
export { refreshAuth } from '../modules/user/refreshAuth';
export { refreshUser } from '../modules/user/refreshUser';
export { updateUser } from '../modules/user/updateUser';
export type { UpdateUserFields } from '../modules/user/user.types';
export { assertWalletAccountSigningAvailability } from '../modules/wallets/assertWalletAccountSigningAvailability';
export { connectAndVerifyWithWalletProvider } from '../modules/wallets/connectAndVerifyWithWalletProvider';
export { connectWithWalletProvider } from '../modules/wallets/connectWithWalletProvider';
export { getAvailableWalletProvidersData } from '../modules/wallets/getAvailableWalletProvidersData';
export { getConnectedAddresses } from '../modules/wallets/getConnectedAddresses';
export { getOwnerWalletAccountForSmartWalletAccount } from '../modules/wallets/getOwnerWalletAccountForSmartWalletAccount';
export { getWalletAccountAddressByType } from '../modules/wallets/getWalletAccountAddressByType';
export { getWalletAccounts } from '../modules/wallets/getWalletAccounts';
export { getWalletProviderDataByKey } from '../modules/wallets/getWalletProviderDataByKey';
export { canConnectWithHardwareWallet } from '../modules/wallets/hardwareWallet/canConnectWithHardwareWallet';
export type { HardwareWalletVendor } from '../modules/wallets/hardwareWallet/hardwareWallet.types';
export { isHardwareWalletAccount } from '../modules/wallets/hardwareWallet/isHardwareWalletAccount';
export { isWalletAccountVerified } from '../modules/wallets/isWalletAccountVerified';
export { addNetwork } from '../modules/wallets/networks/addNetwork';
export { getActiveNetworkData } from '../modules/wallets/networks/getActiveNetworkData';
export { getActiveNetworkId } from '../modules/wallets/networks/getActiveNetworkId';
export { getBalance } from '../modules/wallets/networks/getBalance';
export { getBalanceForAddress } from '../modules/wallets/networks/getBalanceForAddress';
export { getNetworksData } from '../modules/wallets/networks/getNetworksData';
export {
  getTransactionHistory,
  type GetTransactionHistoryParams,
  type GetTransactionHistoryResponse,
} from '../modules/wallets/networks/getTransactionHistory';
export { isProgrammaticNetworkSwitchAvailable } from '../modules/wallets/networks/isProgrammaticNetworkSwitchAvailable';
export type { NetworkProvider } from '../modules/wallets/networks/networkProvider';
export type { NetworkData } from '../modules/wallets/networks/networkProvider/networkProvider.types';
export { switchActiveNetwork } from '../modules/wallets/networks/switchActiveNetwork';
export { getPrimaryWalletAccount } from '../modules/wallets/primaryWalletAccount/getPrimaryWalletAccount';
export { selectPrimaryWalletAccount } from '../modules/wallets/primaryWalletAccount/selectPrimaryWalletAccount';
export { proveWalletAccountOwnership } from '../modules/wallets/proveWalletAccountOwnership';
export { removeWalletAccount } from '../modules/wallets/removeWalletAccount';
export { signMessage } from '../modules/wallets/signMessage';
export { transferAmount } from '../modules/wallets/transferAmount';
export { transferWalletAccount } from '../modules/wallets/transferWalletAccount';
export { assertWalletProviderMethodDefined } from '../modules/wallets/utils/assertWalletProviderMethodDefined';
export { getWalletAccountFromAddress } from '../modules/wallets/utils/getWalletAccountFromAddress';
export { verifyWalletAccount } from '../modules/wallets/verifyWalletAccount';
export type {
  WalletAccount,
  WalletAddressType,
} from '../modules/wallets/walletAccount';
export type {
  TransferAmountParams,
  WalletProviderData,
  WalletProviderMetadata,
} from '../modules/wallets/walletProvider';
export { offWalletProviderEvent } from '../modules/wallets/walletProvider/events/offWalletProviderEvent';
export { onWalletProviderEvent } from '../modules/wallets/walletProvider/events/onWalletProviderEvent';
export { isDeeplinkWalletProvider } from '../modules/wallets/walletProvider/isDeeplinkWalletProvider';
export type { DeviceSigner } from '../services/deviceSigner';
export { parseUserAgent } from '../utils/parseUserAgent';
export { isMobile } from '../utils/isMobile';
